import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { Link, useLoaderData, useSearchParams } from "react-router-dom";

import { useAuth, useNavigation } from "@/providers";
import {
  AlertDialog,
  PageBreadcrumbs,
  PageLayout,
  RobotoButton,
  RobotoLinkHoverUnderline,
} from "@/shared/components";
import {
  ActionParameter,
  ActionRecord,
  InvocationRecord,
  UNSPECIFIED_DATA_SOURCE,
  UploadDestinationType,
} from "@/shared/domain/actions";
import { creditsPerHour } from "@/shared/domain/actions/computeCredits";
import { useAction } from "@/shared/domain/actions/hooks/useAction";
import { OrganizationTier } from "@/shared/domain/orgs";
import { useLazyAPICall } from "@/shared/services/apiHooks";
import {
  APIResponse,
  APIServiceError,
  LimitError,
  invokeActionEndpoint,
} from "@/types";
import { Resource } from "@/types/resources";

import {
  ActionComputeReqs,
  ActionContainerParams,
  ActionInputData,
  ActionSelector,
  DatasetSelector,
} from "../components/action";
import { ActionFilesPreview } from "../components/action/ActionFilesPreview";
import { ActionParamsTable } from "../components/action/ActionParamsTable";
import { ActionTimeoutField } from "../components/action/ActionTimeoutField";
import {
  actionNameError,
  createInitialTimeoutState,
  isActionTimeoutValid,
  parseComputeRequirements,
  parseContainerParameters,
} from "../components/action/actionUtils";

import { InvokeActionLoaderData } from "./loader";

function filterInputData(inputData: string[] | undefined): string[] {
  return inputData?.filter((path) => path.trim().length > 0) ?? [];
}

const formErrors = (
  hasComputeFormErrors: boolean,
  name?: string,
  actionTimeout?: number | string | null,
  datasetId?: string | null,
  inputData?: string[],
) => {
  const hasInputData = filterInputData(inputData).length > 0;
  const hasDataset = !!datasetId;

  return (
    actionNameError(name) ||
    hasComputeFormErrors ||
    !isActionTimeoutValid(actionTimeout) ||
    hasDataset !== hasInputData
  );
};

export const InvokeAction: React.FC = () => {
  const theme = useTheme();

  const { currentOrganization, currentUser } = useAuth();

  const { goto } = useNavigation();

  const [searchParams] = useSearchParams();

  const datasetId = searchParams.get("dataset_id");

  // Initialize the form fields from the loader data
  const formData = useLoaderData() as InvokeActionLoaderData | null;

  const actionQuery = useAction(formData?.actionName, formData?.actionDigest, {
    resourceOwnerId: formData?.actionOwner || currentOrganization?.org_id,
  });

  React.useEffect(() => {
    if (!formData) {
      return;
    }

    const {
      parameters,
      cpu,
      memory,
      gpu,
      storage,
      workdir,
      command,
      entrypoint,
      envVars,
      parameterValues,
      inputData,
      actionTimeout,
    } = formData.initialFormFields;

    setParameters(parameters);
    setCpu(cpu);
    setMemory(memory);
    setGpu(gpu);
    setStorage(storage);
    setWorkdir(workdir);
    setCommand(command);
    setEntrypoint(entrypoint);
    setEnvVars(envVars ?? {});
    setParameterValues(parameterValues);
    setInputData(inputData);
    setActionTimeout(actionTimeout);
  }, [formData]);

  const [inputData, setInputData] = React.useState<string[] | undefined>(
    undefined,
  );

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errorText, setErrorText] = React.useState<string>("");

  const [parameters, setParameters] = React.useState<
    ActionParameter[] | undefined
  >(undefined);

  const [parameterValues, setParameterValues] = React.useState<
    { [key: string]: unknown } | undefined
  >(undefined);

  const [cpu, setCpu] = React.useState<string | undefined>(undefined);
  const [memory, setMemory] = React.useState<string | undefined>(undefined);
  const [gpu, setGpu] = React.useState<string | undefined>(undefined);
  const [storage, setStorage] = React.useState<number | undefined>(undefined);
  const [workdir, setWorkdir] = React.useState<string | undefined>(undefined);
  const [command, setCommand] = React.useState<string | undefined>(undefined);
  const [entrypoint, setEntrypoint] = React.useState<string | undefined>(
    undefined,
  );
  const [envVars, setEnvVars] = React.useState<Record<string, string>>({});
  const [openContainerParams, setOpenContainerParams] =
    React.useState<boolean>(false);
  const [openResourceParams, setOpenResourceParams] =
    React.useState<boolean>(false);

  const [hasComputeFormErrors, setHasComputeFormErrors] = React.useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = React.useState(false);

  const isGated =
    currentOrganization?.tier === OrganizationTier.Free ? true : false;
  const [actionTimeout, setActionTimeout] = React.useState<
    number | string | null | undefined
  >(createInitialTimeoutState(isGated));

  const getErrorText = (error: APIServiceError | null) => {
    if (!error) {
      return "Got an unknown error with no message, please contact support@roboto.ai with details.";
    }

    if (error instanceof LimitError) {
      if (error.resourceName === Resource.FreeTierComputeSpec) {
        return (
          "Free tier orgs can use a max of 4vCPU, 8GB of RAM, 50 GiB of storage space, and no GPUs. " +
          "To upgrade your account, please contact sales@roboto.ai."
        );
      } else if (error.resourceName === Resource.StorageGiBs) {
        return (
          "Free tier orgs can only invoke actions with 50 GiB storage. Lower the storage in the Compute Requirements section. " +
          "To upgrade your account, please contact sales@roboto.ai."
        );
      } else {
        return (
          "You can't invoke any more actions as you've reached your monthly compute quota. " +
          "To upgrade your account, please contact sales@roboto.ai."
        );
      }
    } else {
      return error.message;
    }
  };

  const invokeAction = async () => {
    if (!formData?.actionName) {
      return;
    }

    setErrorText("");
    setLoading(true);

    const computeRequirements = parseComputeRequirements(
      cpu,
      memory,
      gpu,
      storage,
    );
    const containerParameters = parseContainerParameters(
      command,
      entrypoint,
      workdir,
      envVars,
    );

    // Invoke the Action
    const queryParams = formData.actionDigest
      ? new URLSearchParams({ digest: formData.actionDigest })
      : undefined;

    const headers = formData.actionOwner
      ? { "X-Roboto-Resource-Owner-Id": formData.actionOwner }
      : undefined;

    const { error, data } = await invokeActionReq({
      method: "POST",
      endpoint: invokeActionEndpoint,
      pathParams: { actionName: formData.actionName },
      queryParams,
      headers,
      requestBody: JSON.stringify({
        data_source_type: "Dataset",
        data_source_id: datasetId ?? UNSPECIFIED_DATA_SOURCE.data_source_id,
        invocation_source: "Manual",
        invocation_source_id: currentUser?.user_id,
        input_data: filterInputData(inputData),
        compute_requirement_overrides: computeRequirements,
        container_parameter_overrides: containerParameters,
        parameter_values: parameterValues,
        timeout: actionTimeout,
        upload_destination: datasetId && {
          destination_type: UploadDestinationType.Dataset,
          destination_id: datasetId,
        },
      }),
      orgId: currentOrganization?.org_id,
    });

    if (error) {
      setErrorText(getErrorText(error));
    }

    if (!error && data) {
      goto.invocation({
        invocationId: data.data.invocation_id,
      });
    }
    setLoading(false);
  };

  // Replace URL when the action is updated to reflect new query params.
  // Note that base_invocation is implicitly cleared when the action is updated.
  const onActionUpdate = React.useCallback(
    (record: ActionRecord | null) => {
      goto.invokeAction({
        action: {
          name: record?.name ?? "",
          owner: record?.org_id,
          digest: record?.digest,
        },
        datasetId: datasetId ?? undefined,
        inputFiles: inputData,
      });
    },
    [datasetId, goto, inputData],
  );

  // Replace URL when the dataset is updated to reflect new query params.
  // Note that base_invocation is implicitly cleared when the dataset is updated.
  const onDatasetUpdate = React.useCallback(
    (datasetId: string | null) => {
      goto.invokeAction({
        action: {
          name: formData?.actionName ?? "",
          owner: formData?.actionOwner ?? undefined,
          digest: formData?.actionDigest ?? undefined,
        },
        datasetId: datasetId ?? undefined,
        inputFiles: inputData,
      });
    },
    [
      formData?.actionName,
      formData?.actionOwner,
      formData?.actionDigest,
      goto,
      inputData,
    ],
  );

  const { initiateRequest: invokeActionReq } =
    useLazyAPICall<APIResponse<InvocationRecord>>();

  let computeRequirementsTitle = "Compute Requirements (optional)";

  if (currentUser?.is_system_user) {
    computeRequirementsTitle +=
      " (" +
      creditsPerHour(
        parseInt(cpu || "512"),
        parseInt(memory || "1024"),
        storage || 21,
      ).toFixed(2) +
      " credits per hour)";
  }

  const inputEntered =
    datasetId !== null || filterInputData(inputData).length > 0;

  return (
    <PageLayout
      title={"Roboto - Invoke Action"}
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to="/actions?tab=0">Actions</Link>,<span>Invoke Action</span>
        </PageBreadcrumbs>
      }
    >
      <Box>
        <Box
          sx={{
            mb: theme.spacing(2),
          }}
        >
          <Alert severity="info">
            Complete the form below to invoke an action. Check out the{" "}
            <RobotoLinkHoverUnderline
              to="https://docs.roboto.ai/user-guides/index.html"
              target="_blank"
            >
              user guide
            </RobotoLinkHoverUnderline>{" "}
            if it&apos;s your first time.
          </Alert>
        </Box>
        <Box
          component={Paper}
          variant="outlined"
          sx={{ p: theme.spacing(2), mt: theme.spacing(1), background: "none" }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: theme.spacing(10),
            }}
          >
            <Stack
              component="form"
              sx={{
                maxWidth: "75ch",
                width: {
                  xs: "100%",
                  lg: "50%",
                },
              }}
              spacing={3}
              noValidate
              autoComplete="off"
            >
              <Box>
                <Box sx={{ mb: theme.spacing(1.25) }}>
                  <Typography variant="subtitle2">Action</Typography>
                  <Typography variant="caption">
                    Select the action to invoke from your org or the Action Hub.
                  </Typography>
                </Box>
                <ActionSelector
                  action={actionQuery.data || null}
                  setAction={onActionUpdate}
                  currentOrg={currentOrganization}
                />
              </Box>

              <Box>
                <Box sx={{ mb: theme.spacing(1.25) }}>
                  <Typography variant="subtitle2">
                    Dataset {inputEntered ? "" : "(optional)"}
                  </Typography>
                  <Typography variant="caption">
                    Specify which dataset the action should run on.
                  </Typography>
                </Box>
                <DatasetSelector
                  datasetId={datasetId}
                  setDatasetId={onDatasetUpdate}
                  currentOrg={currentOrganization}
                />
              </Box>
              <Box>
                <Box sx={{ mb: theme.spacing(1.25) }}>
                  <Typography variant="subtitle2">
                    Input Data {inputEntered ? "" : "(optional)"}
                  </Typography>
                  <Typography variant="caption">
                    Specify file patterns to include from the dataset, e.g.{" "}
                    <pre style={{ display: "inline-block", fontWeight: 600 }}>
                      **/cam_front/*.jpg
                    </pre>
                    <br />
                    Data will be mounted to{" "}
                    <pre style={{ display: "inline-block", fontWeight: 600 }}>
                      $ROBOTO_INPUT_DIR
                    </pre>{" "}
                    inside the action container.
                  </Typography>
                </Box>

                <ActionInputData
                  inputData={inputData || [""]}
                  setInputData={setInputData}
                  minRequired={1}
                />
              </Box>

              {parameters && parameters.length > 0 && (
                <Box>
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle2">Parameters</Typography>
                    </Box>
                    <Typography variant="caption">
                      Provide values for this action&apos;s parameters.
                      <br />
                      These will be accessible as env vars inside the container
                      as <strong>$ROBOTO_PARAM_*</strong>
                    </Typography>
                  </Box>
                  <ActionParamsTable
                    editable={true}
                    mode="values"
                    params={parameters || []}
                    setParams={setParameters}
                    paramValues={parameterValues}
                    setParamValues={setParameterValues}
                  />
                </Box>
              )}
              <ActionTimeoutField
                isGated={isGated}
                showAlert={true}
                actionTimeout={actionTimeout}
                setActionTimeout={setActionTimeout}
              />
              <Box>
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="subtitle2">
                      Container Parameters (optional)
                    </Typography>
                    <IconButton
                      aria-label="container-params"
                      size="small"
                      sx={{
                        ml: theme.spacing(0.25),
                        transform: openContainerParams
                          ? "rotate(180deg)"
                          : "none",
                      }}
                      onClick={() =>
                        setOpenContainerParams(!openContainerParams)
                      }
                    >
                      <ExpandCircleDownOutlinedIcon
                        color="primary"
                        fontSize="small"
                      />
                    </IconButton>
                  </Box>
                  <Typography variant="caption">
                    Specify parameters to pass to the action&apos;s container at
                    runtime. Pass <i>null</i> to use image defaults.
                  </Typography>
                </Box>
                <Collapse in={openContainerParams} timeout="auto" unmountOnExit>
                  <ActionContainerParams
                    workdir={workdir}
                    setWorkdir={setWorkdir}
                    command={command}
                    setCommand={setCommand}
                    entrypoint={entrypoint}
                    setEntrypoint={setEntrypoint}
                    envVars={envVars}
                    setEnvVars={setEnvVars}
                  />
                </Collapse>
              </Box>
              <Box>
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="subtitle2">
                      {computeRequirementsTitle}
                    </Typography>
                    <IconButton
                      aria-label="compute-requirements"
                      size="small"
                      sx={{
                        ml: theme.spacing(0.25),
                        transform: openResourceParams
                          ? "rotate(180deg)"
                          : "none",
                      }}
                      onClick={() => setOpenResourceParams(!openResourceParams)}
                    >
                      <ExpandCircleDownOutlinedIcon
                        color="primary"
                        fontSize="small"
                      />
                    </IconButton>
                  </Box>
                  <Typography variant="caption">
                    Specify compute resources required to run the action.
                  </Typography>
                </Box>
                <Collapse in={openResourceParams} timeout="auto" unmountOnExit>
                  <ActionComputeReqs
                    cpu={cpu}
                    setCpu={setCpu}
                    memory={memory}
                    setMemory={setMemory}
                    gpu={gpu}
                    setGpu={setGpu}
                    storage={storage}
                    setStorage={setStorage}
                    setHasComputeFormErrors={setHasComputeFormErrors}
                  />
                </Collapse>
              </Box>
            </Stack>
            <ActionFilesPreview inputData={inputData} datasetId={datasetId} />
          </Box>
          <Box sx={{ mt: theme.spacing(3) }}>
            <Box
              sx={{
                mt: theme.spacing(1),
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(2),
              }}
            >
              <RobotoButton
                eventName={"ActionInvoked"}
                eventProperties={{ name: formData?.actionName ?? "" }}
                variant={"contained"}
                color="secondary"
                disabled={
                  !formData?.actionName ||
                  formErrors(
                    hasComputeFormErrors,
                    formData.actionName,
                    actionTimeout,
                    datasetId,
                    inputData,
                  )
                }
                onClick={() => {
                  const filteredInputs = filterInputData(inputData);

                  if (filteredInputs.length === 0) {
                    setAlertDialogOpen(true);
                  } else {
                    void invokeAction();
                  }
                }}
              >
                Invoke Action
              </RobotoButton>
              {loading && (
                <CircularProgress sx={{ ml: theme.spacing(1.5) }} size="1rem" />
              )}
              {errorText && (
                <Typography color="error" variant="caption">
                  {errorText}
                </Typography>
              )}
              <AlertDialog
                dialogOpen={alertDialogOpen}
                handleClose={() => setAlertDialogOpen(false)}
                dialogTitle="Confirm Invocation"
                dialogText={`Are you sure you want to invoke ${formData?.actionName} without any explicit input data?`}
                dialogAction={invokeAction}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </PageLayout>
  );
};
